import React, { useState, useEffect } from 'react';
import { 
  getPendingConnectionRequests, 
  updateConnectionRequest,
  getStartupOpportunityById 
} from '../../services/firebase/startup-opportunities';
import { facilitateConnection } from '../../services/firebase/connection-facilitation';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { useAuthContext } from '../../hooks/useAuthContext';
import { toast } from 'react-hot-toast';

const ConnectionRequestQueue = () => {
  const { user } = useAuthContext();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPendingRequests();
  }, []);

  const fetchPendingRequests = async () => {
    setLoading(true);
    try {
      const result = await getPendingConnectionRequests();
      
      if (result.success) {
        // Enrich requests with user and opportunity data
        const enrichedRequests = await Promise.all(
          result.data.map(async (request) => {
            try {
              // Fetch user profile data
              const db = getFirestore();
              const userProfileRef = doc(db, 'user_profiles', request.userId);
              const userProfileSnap = await getDoc(userProfileRef);
              
              let userData = { id: request.userId, name: 'Unknown User', email: 'Unknown' };
              if (userProfileSnap.exists()) {
                const profile = userProfileSnap.data();
                userData = {
                  id: request.userId,
                  name: profile.basicInfo?.name || profile.displayName || 'Unknown User',
                  email: profile.basicInfo?.email || profile.email || 'Unknown',
                  profileData: profile
                };
              }

              // Fetch opportunity data
              const opportunityResult = await getStartupOpportunityById(request.opportunityId);
              let opportunityData = { id: request.opportunityId, companyName: 'Unknown Company' };
              if (opportunityResult.success) {
                opportunityData = opportunityResult.data;
              }

              return {
                ...request,
                userData,
                opportunityData
              };
            } catch (err) {
              console.error('Error enriching request:', err);
              return {
                ...request,
                userData: { id: request.userId, name: 'Unknown User', email: 'Unknown' },
                opportunityData: { id: request.opportunityId, companyName: 'Unknown Company' }
              };
            }
          })
        );

        setRequests(enrichedRequests);
      } else {
        setError(result.error || 'Failed to fetch connection requests');
      }
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError('Failed to fetch connection requests');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAction = async (requestId, action, notes = '') => {
    setProcessing(true);
    try {
      if (action === 'approved') {
        // For approved requests, facilitate the connection
        const facilitationResult = await facilitateConnection(requestId, {
          adminNotes: notes,
          facilitatedBy: user.email
        });

        if (facilitationResult.success) {
          toast.success('Connection request approved and introduction email sent!');
        } else {
          toast.error(facilitationResult.error || 'Failed to facilitate connection');
        }
      } else {
        // For declined requests, just update the status
        const updateData = {
          status: action,
          adminNotes: notes,
          processedAt: new Date(),
          processedBy: user.email
        };

        const result = await updateConnectionRequest(requestId, updateData);
        
        if (result.success) {
          toast.success(`Connection request ${action} successfully`);
        } else {
          toast.error(result.error || `Failed to ${action} connection request`);
        }
      }

      // Refresh the requests list
      await fetchPendingRequests();
      setSelectedRequest(null);
      setAdminNotes('');
    } catch (err) {
      console.error('Error processing request:', err);
      toast.error(`Failed to ${action} connection request`);
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-2 text-gray-600">Loading connection requests...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
            <div className="mt-4">
              <button
                onClick={fetchPendingRequests}
                className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Connection Request Queue</h1>
          <p className="mt-2 text-sm text-gray-700">
            Review and manage connection requests between candidates and startup founders.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={fetchPendingRequests}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          >
            Refresh
          </button>
        </div>
      </div>

      {requests.length === 0 ? (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No pending requests</h3>
          <p className="mt-1 text-sm text-gray-500">
            All connection requests have been processed.
          </p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul role="list" className="divide-y divide-gray-200">
            {requests.map((request) => (
              <li key={request.id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-purple-600">
                            {request.userData.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {request.userData.name}
                          </p>
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {request.status}
                          </span>
                        </div>
                        <div className="mt-2 flex">
                          <div className="flex items-center text-sm text-gray-500">
                            <p className="truncate">
                              Wants to connect with <span className="font-medium">{request.opportunityData.companyName}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setSelectedRequest(request)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                      >
                        Review
                      </button>
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="sm:flex">
                      <div className="sm:flex-1">
                        <p className="text-xs text-gray-500">
                          Submitted: {formatDate(request.createdAt)}
                        </p>
                        <p className="text-xs text-gray-500">
                          Email: {request.userData.email}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Request Detail Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Connection Request Review
                </h3>
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* User Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Candidate Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="text-sm font-medium text-gray-900">{selectedRequest.userData.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="text-sm font-medium text-gray-900">{selectedRequest.userData.email}</p>
                    </div>
                  </div>
                </div>

                {/* Opportunity Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Opportunity Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Company</p>
                      <p className="text-sm font-medium text-gray-900">{selectedRequest.opportunityData.companyName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Role Area</p>
                      <p className="text-sm font-medium text-gray-900">{selectedRequest.opportunityData.roleArea || 'Not specified'}</p>
                    </div>
                  </div>
                  {selectedRequest.opportunityData.fundingStage && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-600">Funding Stage</p>
                      <p className="text-sm font-medium text-gray-900">{selectedRequest.opportunityData.fundingStage}</p>
                    </div>
                  )}
                </div>

                {/* User Message */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Candidate Message</h4>
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <p className="text-sm text-gray-700">
                      {selectedRequest.userMessage || 'No message provided'}
                    </p>
                  </div>
                </div>

                {/* Admin Notes */}
                <div>
                  <label htmlFor="admin-notes" className="block text-sm font-medium text-gray-700 mb-2">
                    Admin Notes
                  </label>
                  <textarea
                    id="admin-notes"
                    rows={3}
                    className="shadow-sm focus:ring-purple-500 focus:border-purple-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Add notes about this connection request..."
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setSelectedRequest(null)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    disabled={processing}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleRequestAction(selectedRequest.id, 'declined', adminNotes)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    disabled={processing}
                  >
                    {processing ? 'Processing...' : 'Decline'}
                  </button>
                  <button
                    onClick={() => handleRequestAction(selectedRequest.id, 'approved', adminNotes)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    disabled={processing}
                  >
                    {processing ? 'Processing...' : 'Approve'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectionRequestQueue;